import {
  ADD_CATEGORIES,
  ADD_CONTEXT_TAGS,
  ADD_OPEN_SECTION_ID,
  CLEAR_DOCUMENT,
  CLEAR_DOCUMENTS_META,
  CLEAR_VIEW_STACK,
  FETCHING_DOCUMENTS_META_FAILURE,
  FETCHING_DOCUMENTS_META_STARTED,
  FETCHING_DOCUMENTS_META_SUCCESS,
  FETCHING_DOCUMENT_FAILURE,
  FETCHING_DOCUMENT_STARTED,
  FETCHING_DOCUMENT_SUCCESS,
  POP_VIEW,
  PUSH_VIEW,
  REMOVE_CONTEXT_TAGS,
  REMOVE_OPEN_SECTION_ID,
  REMOVE_VIEW,
  SET_CATEGORY,
  SET_POINTS_OF_INTEREST,
  SHOW_WIKI_SIDE_BAR
} from 'src/actions/WikiActions'
import { WikiDocument } from 'src/components/wiki/wikidocument/WikiDocument'
import { BaseAction, CallResult, DataAction, MessageAction } from 'src/CustomTypes'
import { ContextTag, WikiDocumentMeta, WikiLocation } from 'src/dataSources/wiki/Wiki'

export enum WikiView {
  WikiBrowser,
  WikiDocument,
  WikiCategory
}

export interface WikiState {
  fetchDocumentsMetaStatus: CallResult
  fetchDocumentsMetaError: string
  fetchDocumentStatus: CallResult
  fetchDocumentError: string
  document?: WikiDocument
  openSectionIds: string[]
  documentsMeta: WikiDocumentMeta[]
  contextTags: ContextTag[]
  categories: string[]
  poi: WikiLocation[]
  category: string
  showWikiSideBar: boolean
  currentWikiViews: WikiView[]
}

export const wikiStateInitial: WikiState = {
  fetchDocumentsMetaStatus: CallResult.Initial,
  fetchDocumentsMetaError: '',
  fetchDocumentStatus: CallResult.Initial,
  fetchDocumentError: '',
  document: undefined,
  openSectionIds: [],
  documentsMeta: [],
  contextTags: [ContextTag.OSM, ContextTag.MODEL_AREAS],
  categories: [],
  poi: [],
  category: '',
  showWikiSideBar: false,
  currentWikiViews: []
}

export function wikiReducer (
  state: WikiState = wikiStateInitial, // eslint-disable-line @typescript-eslint/default-param-last
  action: BaseAction): WikiState {
  switch (action.type) {
    case FETCHING_DOCUMENTS_META_STARTED: {
      return {
        ...state,
        fetchDocumentsMetaStatus: CallResult.Running
      }
    }
    case FETCHING_DOCUMENTS_META_FAILURE: {
      const a = action as MessageAction
      return {
        ...state,
        fetchDocumentsMetaStatus: CallResult.Failed,
        fetchDocumentsMetaError: a.message
      }
    }
    case FETCHING_DOCUMENTS_META_SUCCESS: {
      const a = action as DataAction<WikiDocumentMeta[]>
      return {
        ...state,
        fetchDocumentsMetaStatus: CallResult.Succeeded,
        documentsMeta: a.data
      }
    }
    case FETCHING_DOCUMENT_STARTED: {
      return {
        ...state,
        fetchDocumentStatus: CallResult.Running
      }
    }
    case FETCHING_DOCUMENT_FAILURE: {
      const a = action as MessageAction
      return {
        ...state,
        fetchDocumentStatus: CallResult.Failed,
        fetchDocumentError: a.message
      }
    }
    case FETCHING_DOCUMENT_SUCCESS: {
      const a = action as DataAction<WikiDocument>
      return {
        ...state,
        fetchDocumentStatus: CallResult.Succeeded,
        document: a.data
      }
    }
    case ADD_OPEN_SECTION_ID: {
      const a = action as DataAction<string>
      const newOpenSectionsIds: string[] = Object.assign([], state.openSectionIds)
      return {
        ...state,
        openSectionIds: [a.data, ...newOpenSectionsIds.filter(id => id !== a.data)]
      }
    }
    case REMOVE_OPEN_SECTION_ID: {
      const a = action as DataAction<string>
      const newOpenSectionsIds: string[] = Object.assign([], state.openSectionIds)
      return {
        ...state,
        openSectionIds: newOpenSectionsIds.filter(id => id !== a.data)
      }
    }
    case ADD_CONTEXT_TAGS: {
      const a = action as DataAction<ContextTag[]>
      return {
        ...state,
        contextTags: [...state.contextTags, ...a.data.filter(tag => !state.contextTags.includes(tag))]
      }
    }
    case REMOVE_CONTEXT_TAGS: {
      const a = action as DataAction<ContextTag[]>
      const newContextTags: ContextTag[] = Object.assign([], state.contextTags)
      a.data.forEach(tagToBeRemoved => {
        const index = newContextTags.findIndex(tag => tag === tagToBeRemoved)
        if (index !== -1) {
          newContextTags.splice(index, 1)
        }
      })
      return {
        ...state,
        contextTags: newContextTags
      }
    }
    case ADD_CATEGORIES: {
      const a = action as DataAction<string[]>
      return {
        ...state,
        categories: a.data
      }
    }
    case SET_POINTS_OF_INTEREST: {
      const a = action as DataAction<WikiLocation[]>
      return {
        ...state,
        poi: a.data
      }
    }
    case SET_CATEGORY: {
      const a = action as DataAction<string>
      return {
        ...state,
        category: a.data
      }
    }
    case PUSH_VIEW: {
      const a = action as DataAction<WikiView>
      return {
        ...state,
        currentWikiViews: [...state.currentWikiViews, a.data]
      }
    }
    case POP_VIEW: {
      return {
        ...state,
        currentWikiViews: state.currentWikiViews.slice(0, -1)
      }
    }
    case REMOVE_VIEW: {
      const a = action as DataAction<WikiView>
      return {
        ...state,
        currentWikiViews: state.currentWikiViews.filter(view => view !== a.data)
      }
    }
    case CLEAR_VIEW_STACK: {
      return {
        ...state,
        currentWikiViews: []
      }
    }
    case SHOW_WIKI_SIDE_BAR: {
      const a = action as DataAction<boolean>
      return {
        ...state,
        showWikiSideBar: a.data
      }
    }
    case CLEAR_DOCUMENTS_META: {
      return {
        ...state,
        documentsMeta: []
      }
    }
    case CLEAR_DOCUMENT: {
      return {
        ...state,
        document: undefined,
        openSectionIds: [],
        fetchDocumentStatus: CallResult.Initial
      }
    }
    default:
      return state
  }
}
